export const TABS = {
    SETTINGS: 'settings',
    SCANS: 'scans',
};

export const TAB_LABELS = {
    [TABS.SETTINGS]: 'common.settings',
    [TABS.SCANS]: 'common.scans',
};

export const EVENTS = {
    CREATE_DOMAIN: 'CreateDomain',
    DELETE_DOMAIN: 'DeleteDomain',
    DOMAINS_FILTER: 'DomainsFilter',
    TRIGGER_DOMAIN_SCAN: 'TriggerDomainScan',
    UPDATE_DOMAIN: 'UpdateDomain',
};

export const DOMAIN_URL_TYPES = {
    // COOKIE_POLICY: 'COOKIE_POLICY',
    PRIVACY_POLICY: 'PRIVACY_POLICY',
    // STYLESHEET: 'STYLESHEET',
};

export const DOMAIN_URL_SITEMAP_KEYS = {
    // COOKIE_POLICY: 'cookiePolicyUrls',
    PRIVACY_POLICY: 'privacyPolicyUrls',
    // STYLESHEET: 'stylesheetUrls',
};

export const DOMAIN_URL_TYPE_LABELS = {
    // [DOMAIN_URL_TYPES.COOKIE_POLICY]: 'common.cookiePolicy',
    [DOMAIN_URL_TYPES.PRIVACY_POLICY]: 'common.privacyPolicy',
    // [DOMAIN_URL_TYPES.STYLESHEET]: 'common.stylesheet',
};

export const SCAN_FREQUENCIES = {
    NONE: 0,
    DAILY: 1,
    WEEKLY: 7,
    MONTHLY: 30,
};

export const SCAN_FREQUENCY_LABELS = {
    [SCAN_FREQUENCIES.DAILY]: 'common.frequency.daily',
    [SCAN_FREQUENCIES.WEEKLY]: 'common.frequency.weekly',
    [SCAN_FREQUENCIES.MONTHLY]: 'common.frequency.monthly',
};

const SUPPORTED_SCAN_FREQUENCIES = Object.keys(SCAN_FREQUENCY_LABELS).map(Number);
const DEFAULT_SCAN_FREQUENCY = SCAN_FREQUENCIES.WEEKLY;

export const getAllowedScanFrequencies = minimumFrequencyDays => {
    // null represents unlimited — all frequencies allowed.
    if (minimumFrequencyDays === null) {
        return [...SUPPORTED_SCAN_FREQUENCIES];
    }

    const parsedMinimumFrequencyDays = Number(minimumFrequencyDays);
    const minimum =
        Number.isFinite(parsedMinimumFrequencyDays) && parsedMinimumFrequencyDays > 0
            ? parsedMinimumFrequencyDays
            : DEFAULT_SCAN_FREQUENCY;

    const filteredFrequencies = SUPPORTED_SCAN_FREQUENCIES.filter(
        frequency => frequency >= minimum
    );

    if (filteredFrequencies.length > 0) {
        return filteredFrequencies;
    }

    return [SUPPORTED_SCAN_FREQUENCIES[SUPPORTED_SCAN_FREQUENCIES.length - 1]];
};

export const SCAN_REQUIREMENTS = {
    COOKIE_BANNER_PRESENCE: 'cookieBannerPresentCheck',
    COOKIE_BANNER_BUTTONS: 'cookieBannerButtonsCheck',
    PRIVACY_POLICY_FRESHNESS: 'privacyPolicyFreshCheck',
    PRIVACY_POLICY_PRESENCE: 'privacyPolicyPresentCheck',
    SUBJECT_RIGHTS_METHOD: 'subjectRightsMethodCheck',
    GLOBAL_PRIVACY_CONTROL: 'globalPrivacyControlCheck',
    REJECT_ALL: 'rejectAllCheck',
    FIRST_LOAD: 'firstLoadCheck',
};

export const CC_REJECT_ALL_CHECK_NAME = 'reject_all_check';

// Unclassified trackers still present after reject all that tip a passing check into a
// warning. Counted in distinct tracker domains, not artifact rows — a single domain can fire
// many requests, and real scans see roughly 4-9 rows per domain (CCM-576). The previous
// value of 100 predates that being consistent: the domain-list status column already
// compared domain counts against it, so that warning could not fire at all on a typical
// scan of 30-40 distinct domains.
export const UNCLASSIFIED_WARNING_THRESHOLD = 15;

export const REJECT_ALL_DISPLAY_CATEGORIES = [
    'marketing',
    'analytics',
    'personalization',
    'essential',
    'unclassified',
];

export const NON_ESSENTIAL_CATEGORIES = ['marketing', 'analytics', 'personalization'];

export const SCAN_SOURCES = {
    MANUAL: 'manual',
    DASH_APP: 'dash_app',
    HUBSPOT: 'hubspot',
    AUTOMATIC: 'automatic',
};

export const SCAN_STATUSES = {
    PENDING: 'pending',
    RUNNING: 'running',
    COMPLETED: 'completed',
    FAILED: 'failed',
    CANCELLED: 'cancelled',
};

export const CHECK_IN_PROGRESS = 'in_progress';

export const SCAN_IN_PROGRESS_STATUSES = [SCAN_STATUSES.PENDING, SCAN_STATUSES.RUNNING];

export const SCAN_STATUS_LABELS = {
    [SCAN_STATUSES.PENDING]: 'common.status.pending',
    [SCAN_STATUSES.RUNNING]: 'common.status.inProgress',
    [SCAN_STATUSES.COMPLETED]: 'common.status.completed',
    [SCAN_STATUSES.FAILED]: 'common.status.failed',
    [SCAN_STATUSES.CANCELLED]: 'common.status.cancelled',
};

export const SCAN_JURISDICTIONS = {
    CALIFORNIA: 'us-ca',
};

export const SCAN_ERROR_CODES = {
    NAVIGATION_FAILED: 'S007',
    GPC_PAGE_BLOCKED: 'S011',
    SITE_UNREACHABLE: 'S025',
    SITE_NOT_LOADED: 'S026',
    SITE_BLOCKED: 'S027',
    SITE_AUTH_REQUIRED: 'S028',
    ROBOTS_DISALLOWED: 'S030',
};

export const GPC_SPIDER_ERROR_CODES = new Set([
    SCAN_ERROR_CODES.NAVIGATION_FAILED,
    SCAN_ERROR_CODES.GPC_PAGE_BLOCKED,
    SCAN_ERROR_CODES.ROBOTS_DISALLOWED,
]);

export const ROBOTS_SETUP_GUIDE_URL =
    'https://developers.osano.com/compliance-check/verified-bots#allow-access-in-robotstxt';

export const PAGE_BLOCKED_ERROR_PATH = 'domains.siteScan.errors.pageBlocked';

export const GPC_DEFAULT_ERROR_DESCRIPTION_PATH =
    'domains.siteScan.globalPrivacyControlCheck.errorDescription';

export const SCAN_FAILED_DESCRIPTION_PATH = 'domains.siteScan.errors.scanFailed';

export const SCAN_FAILED_REASON_DESCRIPTION_PATH = 'domains.siteScan.errors.scanFailedReason';

// Why a whole scan ended, as opposed to why one check did. The scanner stamps one of these when the
// site never served it a page, and we say which rather than the generic failure line.
export const SCAN_FAILURE_DESCRIPTION_PATHS = {
    [SCAN_ERROR_CODES.ROBOTS_DISALLOWED]: 'domains.siteScan.errors.robotsDisallowed',
    [SCAN_ERROR_CODES.SITE_UNREACHABLE]: 'domains.siteScan.errors.siteUnreachable',
    [SCAN_ERROR_CODES.SITE_NOT_LOADED]: 'domains.siteScan.errors.siteNotLoaded',
    [SCAN_ERROR_CODES.SITE_BLOCKED]: 'domains.siteScan.errors.siteBlocked',
    [SCAN_ERROR_CODES.SITE_AUTH_REQUIRED]: 'domains.siteScan.errors.siteAuthRequired',
};

export const GPC_ERROR_DESCRIPTION_PATHS = {
    [SCAN_ERROR_CODES.ROBOTS_DISALLOWED]: 'domains.siteScan.errors.robotsDisallowed',
    [SCAN_ERROR_CODES.NAVIGATION_FAILED]:
        'domains.siteScan.globalPrivacyControlCheck.navigationFailedErrorDescription',
    [SCAN_ERROR_CODES.GPC_PAGE_BLOCKED]: PAGE_BLOCKED_ERROR_PATH,
};

export const REJECT_ALL_ERROR_DESCRIPTIONS = [
    {
        match: 'cmp_not_found',
        path: 'domains.siteScan.rejectAllCheck.errors.cmpNotFound',
    },
    {
        match: 'popup_not_visible',
        path: 'domains.siteScan.rejectAllCheck.errors.popupNotVisible',
    },
    {
        match: 'opt_out_failed',
        path: 'domains.siteScan.rejectAllCheck.errors.optOutFailed',
    },
    {
        match: 'page_blocked',
        path: PAGE_BLOCKED_ERROR_PATH,
    },
    {
        match: 'page_not_loaded',
        path: 'domains.siteScan.rejectAllCheck.errors.pageNotLoaded',
    },
];

export const SCAN_REQUIREMENT_MAPPING = {
    [SCAN_REQUIREMENTS.COOKIE_BANNER_PRESENCE]: {
        order: 1,
        fieldPaths: {
            reasoning: 'reasoning',
        },
    },
    [SCAN_REQUIREMENTS.COOKIE_BANNER_BUTTONS]: {
        order: 2,
        fieldPaths: {
            prominenceExplanation: 'buttonDetails.visualVerification.prominenceExplanation',
        },
    },
    [SCAN_REQUIREMENTS.PRIVACY_POLICY_PRESENCE]: {
        order: 3,
        fieldPaths: {
            pages: 'pages',
        },
    },
    [SCAN_REQUIREMENTS.PRIVACY_POLICY_FRESHNESS]: {
        order: 4,
        fieldPaths: {
            lastUpdatedDate: 'lastUpdatedDate',
            requiredLastUpdate: 'requiredLastUpdate',
            reasoning: 'reasoning',
        },
    },
    [SCAN_REQUIREMENTS.SUBJECT_RIGHTS_METHOD]: {
        order: 5,
        fieldPaths: {
            accessMethods: 'accessMethods',
            reasoning: 'reasoning',
        },
    },
    [SCAN_REQUIREMENTS.GLOBAL_PRIVACY_CONTROL]: {
        order: 6,
        fieldPaths: {
            summary: 'summary',
            gpcJson: 'gpcJson',
            signalVisual: 'signalVisual',
            googleConsent: 'googleConsent',
            trackerAnalysis: 'trackerAnalysis',
            signalAnalysis: 'signalAnalysis',
            cookieAnalysis: 'cookieAnalysis',
            requestAnalysis: 'requestAnalysis',
            usPrivacyString: 'usPrivacyString',
            gppString: 'gppString',
        },
    },
    [SCAN_REQUIREMENTS.REJECT_ALL]: {
        order: 7,
        fieldPaths: {
            categorySummaries: 'categorySummaries',
            trackerAnalysis: 'trackerAnalysis',
        },
    },
    [SCAN_REQUIREMENTS.FIRST_LOAD]: {
        order: 8,
        fieldPaths: {
            summary: 'summary',
            suggestedAction: 'suggestedAction',
        },
    },
};
